<script setup lang="ts">
  import { ref, onMounted, watch, nextTick } from 'vue'
  import { Modal, Card, Button, Spin, Input, Checkbox } from 'ant-design-vue'
  import { PlusOutlined, SearchOutlined, WarningFilled } from '@ant-design/icons-vue'
  import { CalculationSchema } from '../models/CalculationSchema'

  const props = defineProps<{
    visible: boolean
    calculationSchemas: Array<CalculationSchema>
  }>()

  const emit = defineEmits<{
    'update:visible': [value: boolean]
    'calculation-selected': [calculation: CalculationSchema]
  }>()

  const availableCalculations = ref<CalculationSchema[]>([])
  const filteredCalculations = ref<CalculationSchema[]>([])
  const showHiddenCalculations = ref<boolean>(false)
  const loadingCalculations = ref<boolean>(false)
  const hasErrorLoadingCalculations = ref<boolean>(false)
  const searchText = ref<string>('')
  const searchInputRef = ref<InstanceType<typeof Input> | null>(null)

  // Group calculations by category
  const groupedCalculations = ref<Record<string, CalculationSchema[]>>({})

  const onSearchTextChanged = (event: InputEvent): void => {
    const target = event.target as HTMLInputElement
    const value = target.value
    const search = value?.toLowerCase().trim()

    if (search?.length > 0) {
      filteredCalculations.value = availableCalculations.value.filter(
        (schema: CalculationSchema) => {
          return (
            (schema.description && schema.description.toLowerCase().includes(search)) ||
            (schema.display_name && schema.display_name.toLowerCase().includes(search))
          )
        }
      )
    } else {
      filteredCalculations.value = [...availableCalculations.value]
    }
    groupCalculationsByCategory()
  }

  const groupCalculationsByCategory = () => {
    const grouped: Record<string, CalculationSchema[]> = {}
    filteredCalculations.value.forEach((calc) => {
      if (!grouped[calc.category]) {
        grouped[calc.category] = []
      }
      grouped[calc.category].push(calc)
    })
    groupedCalculations.value = grouped
  }

  const selectCalculation = (calculation: CalculationSchema) => {
    emit('calculation-selected', calculation)
    // Keep dialog open to allow adding multiple calculations easily
  }

  const handleCancel = () => {
    emit('update:visible', false)
  }

  onMounted(() => {
    resetAvailableCalcultions()
  })

  const handleVisibilityChange = (visible: boolean) => {
    if (visible) {
      resetAvailableCalcultions()
      nextTick(() => {
        // reset the search model (don't overwrite the Input ref)
        searchText.value = ''

        // try to call component focus() first
        const comp = searchInputRef.value as any
        if (comp?.focus && typeof comp.focus === 'function') {
          comp.focus()
          return
        }

        // fallback: find the native input element and focus it
        const el = comp?.$el ?? comp
        const nativeInput = (el as HTMLElement | null)?.querySelector?.(
          'input'
        ) as HTMLInputElement | null
        nativeInput?.focus()
      })
    }
  }

  const resetAvailableCalcultions = () => {
    availableCalculations.value = props.calculationSchemas
    filteredCalculations.value = props.calculationSchemas
    groupCalculationsByCategory()
  }

  watch(
    () => props.visible,
    (newValue: boolean) => {
      handleVisibilityChange(newValue)
    }
  )

  watch(
    () => props.calculationSchemas,
    (newItems) => {
      availableCalculations.value = [...newItems]
      filteredCalculations.value = [...newItems]
      groupCalculationsByCategory()
    }
  )

  defineExpose({
    availableCalculations
  })
</script>

<template>
  <Modal
    :open="visible"
    title="Add Calculation"
    width="1000px"
    :bodyStyle="{ minHeight: '60vh' }"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="calculation-selection-content">
      <!-- Search Bar -->
      <div class="mb-4 flex items-center pr-2">
        <Input
          ref="searchInputRef"
          v-model:value="searchText"
          placeholder="Search calculations by name or description..."
          size="large"
          @input="onSearchTextChanged"
        >
          <template #prefix>
            <SearchOutlined />
          </template>
        </Input>
        <Checkbox
          class="ml-2 flex-shrink-0 whitespace-nowrap"
          v-model:checked="showHiddenCalculations"
        >
          Show All Calculations
        </Checkbox>
      </div>

      <!-- Loading State -->
      <div v-if="loadingCalculations" class="py-8 text-center">
        <Spin size="large" />
        <p class="mt-2">Loading available calculations...</p>
      </div>

      <!-- Calculations List -->
      <div v-else id="calculationsListContainer" class="max-h-[60vh] overflow-y-auto">
        <div
          v-if="!hasErrorLoadingCalculations"
          v-for="(calculations, category) in groupedCalculations"
          :key="category"
          class="mb-6"
        >
          <!-- Only show category if it has calculations (after filtering) -->
          <template
            v-if="
              calculations.filter((x) => (showHiddenCalculations ? x : x.is_visible === true))
                .length > 0
            "
          >
            <h3 class="mb-3 border-b pb-1 text-lg font-semibold">
              {{ category }}
              <span v-if="searchText.trim()" class="ml-2 text-sm font-normal">
                ({{ calculations.length }}
                {{ calculations.length === 1 ? 'calculation' : 'calculations' }})
              </span>
            </h3>

            <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Card
                v-for="calculation in calculations.filter((x) =>
                  showHiddenCalculations ? x : x.is_visible === true
                )"
                :key="calculation.type"
                class="cursor-pointer transition-all duration-200 hover:border-blue-400 hover:shadow-md"
                size="small"
                @click="selectCalculation(calculation)"
              >
                <div class="p-2">
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <h4 class="mb-1 text-sm font-medium">{{ calculation.display_name }}</h4>
                      <p class="mb-2 text-xs dark:text-[var(--white-50)]">
                        {{ calculation.description }}
                      </p>
                      <div class="flex items-center text-xs">
                        <span
                          class="rounded bg-blue-100 px-2 py-1 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                        >
                          {{ calculation.fields.length }} field{{
                            calculation.fields.length !== 1 ? 's' : ''
                          }}
                        </span>
                      </div>
                    </div>
                    <PlusOutlined class="ml-2 text-blue-500" />
                  </div>
                </div>
              </Card>
            </div>
          </template>
        </div>
        <div
          v-else-if="hasErrorLoadingCalculations"
          class="mt-1 mt-[20vh] flex justify-center text-red-400"
        >
          <WarningFilled class="mr-2 text-2xl" />
          An error was encountered loading calculations.
        </div>
        <!-- No Results -->
        <div
          v-if="!hasErrorLoadingCalculations && filteredCalculations.length === 0"
          class="py-8 text-center"
        >
          <p>
            {{
              searchText.trim()
                ? 'No calculations found matching your search.'
                : 'No calculations available.'
            }}
          </p>
          <p v-if="searchText.trim()" class="mt-1 text-xs">
            Try adjusting your search terms or clear the search to browse all calculations.
          </p>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="mt-6 flex items-center justify-between border-t pt-4">
        <div class="text-sm">
          Click on any calculation above to add it to your calculations pipeline
        </div>
        <div class="flex gap-2">
          <Button @click="handleCancel">Cancel</Button>
          <Button type="primary" @click="handleCancel">Close</Button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
  .calculation-selection-content {
    min-height: 60vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  #calculationsListContainer {
    flex: 1;
  }
  #calculationsListContainer::-webkit-scrollbar {
    width: 6px;
  }
  #calculationsListContainer::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  #calculationsListContainer::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  #calculationsListContainer::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
</style>
